import { DictationMic } from '@/components/shared/DictationMic'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import { ANSWER_TEXT_MAX_CHARS } from '@/lib/constants'
import { isProbablyMobile } from '@/lib/device'
import { getErrorMessage } from '@/lib/errors'
import { extractEdgeError } from '@/lib/invokeEdge'
import { scoreBarColor, scoreColor } from '@/lib/scoreColors'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useCallback, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

// ─── Coaching tips ────────────────────────────────────────────────────────────

const STAR_TIPS: Record<string, string> = {
  situation:
    'Set the scene specifically — name the project, team size, timeline, and stakes before diving into what you did.',
  task: 'Make your responsibility crystal clear. What was expected of YOU specifically, and why did it fall to you?',
  action:
    "Use 'I' not 'we'. Walk through exactly what you did, step by step, and why you made each decision.",
  result:
    "End with a number. What metric changed? By how much? Over what time period? Qualitative outcomes alone aren't enough.",
}

const COMPETENCY_TIPS: Record<string, string> = {
  leadership:
    'Show how you influenced direction even without formal authority. Whose behavior changed because of you?',
  teamwork:
    "Describe the specific dynamic you navigated, not just 'we collaborated'. What tension existed?",
  communication:
    'Pick a moment where clarity was the key variable. How did you adapt your message to the audience?',
  problem_solving:
    'Walk through your diagnostic process. What did you gather first, and how did you narrow to a solution?',
  time_management:
    'Quantify the trade-offs. What did you deprioritize, and how did you decide what could wait?',
  stakeholder_management:
    'Name the stakeholders and their conflicting interests. How did you align them?',
  attention_to_detail:
    "Describe the mechanism you used — checklist, review process, tool — not just that you 'double-checked'.",
}

function getTip(type: string, key: string): string {
  if (type === 'star_component') return STAR_TIPS[key] ?? ''
  return COMPETENCY_TIPS[key] ?? ''
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface StarScores {
  situation_score: number
  task_score: number
  action_score: number
  result_score: number
  overall_score: number
}

interface PerComponentFeedback {
  situationFeedback?: string
  taskFeedback?: string
  actionFeedback?: string
  resultFeedback?: string
}

type DrillPhase = 'intro' | 'loading' | 'questioning' | 'feedback' | 'error'

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreBar({ label, score, weight }: { label: string; score: number; weight: string }) {
  const pct = Math.round(((score - 1) / 4) * 100)
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 text-xs text-muted-foreground flex items-center justify-between shrink-0">
        <span>{label}</span>
        <span className="text-[10px] opacity-50">{weight}</span>
      </div>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', scoreBarColor(score))}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn('text-sm font-semibold w-8 text-right tabular-nums', scoreColor(score))}>
        {score.toFixed(1)}
      </span>
    </div>
  )
}

function CoachingTip({ tip }: { tip: string }) {
  if (!tip) return null
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
      💡 {tip}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DrillPage() {
  const [searchParams] = useSearchParams()
  const role = searchParams.get('role') ?? ''
  const difficulty = (searchParams.get('difficulty') ?? 'entry') as 'entry' | 'mid' | 'senior'
  const type = searchParams.get('type') ?? 'star_component'
  const key = searchParams.get('key') ?? 'result'
  const score = Number.parseFloat(searchParams.get('score') ?? '0')
  const label = searchParams.get('label') ?? key

  const tip = useMemo(() => getTip(type, key), [type, key])

  const [phase, setPhase] = useState<DrillPhase>('intro')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [currentQuestion, setCurrentQuestion] = useState<{ id: string; text: string } | null>(null)
  const [isFollowUp, setIsFollowUp] = useState(false)
  const [probeCount, setProbeCount] = useState(0)
  const [draft, setDraft] = useState('')
  const [scores, setScores] = useState<StarScores | null>(null)
  const [feedback, setFeedback] = useState<PerComponentFeedback | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [roundCount, setRoundCount] = useState(0)

  const { trackSpeechToTextUsed } = useAnalytics()
  const {
    isListening,
    isSupported,
    interimTranscript,
    error: sttError,
    startListening,
    stopListening,
  } = useSpeechToText({
    onFinalTranscript: (text) => {
      setDraft((prev) => {
        const trimmed = prev.trimEnd()
        return trimmed ? `${trimmed} ${text}` : text
      })
    },
  })
  // Web Speech is unreliable on mobile — hide the mic there (see src/lib/device.ts)
  const canDictate = isSupported && !isProbablyMobile()

  const handleMicClick = useCallback(() => {
    if (isListening) {
      stopListening()
    } else {
      startListening()
      // probeCount is the closest sequence analog Drill mode exposes (no questionIndex)
      if (sessionId) {
        trackSpeechToTextUsed({ session_id: sessionId, question_sequence: probeCount })
      }
    }
  }, [isListening, startListening, stopListening, sessionId, probeCount, trackSpeechToTextUsed])

  async function startDrill() {
    setPhase('loading')
    setScores(null)
    setFeedback(null)
    setProbeCount(0)
    setDraft('')
    setIsFollowUp(false)

    const body: Record<string, unknown> = {
      action: 'start',
      targetRole: role,
      difficulty,
      drillMode: true,
    }
    if (type === 'star_component') body.focusStarComponent = key
    if (type === 'competency') body.focusCompetency = key

    const { data, error } = await supabase.functions.invoke('interview-engine', { body })

    if (error || !data?.sessionId) {
      const { code, message } = await extractEdgeError(error)
      setErrorMsg(getErrorMessage(code, message))
      setPhase('error')
      return
    }

    setSessionId(data.sessionId)
    setCurrentQuestion({ id: data.questionId, text: data.questionText })
    setPhase('questioning')
  }

  async function submitAnswer() {
    if (!sessionId || !currentQuestion || !draft.trim()) return
    setErrorMsg('')
    setPhase('loading')

    const { data, error } = await supabase.functions.invoke('interview-engine', {
      body: {
        action: 'answer',
        sessionId,
        questionId: currentQuestion.id,
        answerText: draft.trim(),
        probeCount,
      },
    })

    if (error || !data) {
      // Back to 'questioning', not 'error': the 'error' phase early-returns a
      // full-screen state whose only action starts a brand-new drill, so the
      // question and the typed answer were both lost with no way to resend.
      const { code, message } = await extractEdgeError(error)
      setErrorMsg(getErrorMessage(code, message))
      setPhase('questioning')
      return
    }

    setErrorMsg('')

    if (data.scores) {
      setScores(data.scores)
      setFeedback(data.feedback ?? null)
    }

    if (data.followUp) {
      setCurrentQuestion({ id: data.followUp.questionId, text: data.followUp.questionText })
      setIsFollowUp(true)
      setProbeCount((c) => c + 1)
      setDraft('')
      setPhase('questioning')
    } else {
      setRoundCount((c) => c + 1)
      setPhase('feedback')
    }
  }

  const title = type === 'star_component' ? `Strengthen your ${label}` : `Practice ${label}`

  // ── Intro ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-background">
        <div className="w-full max-w-lg space-y-6">
          <div className="space-y-1">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Drill Mode
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {score > 0 && (
              <p className="text-sm text-muted-foreground">
                Your current average:{' '}
                <span className={scoreColor(score)}>{score.toFixed(1)}/5</span>
              </p>
            )}
          </div>

          <CoachingTip tip={tip} />

          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground space-y-1">
            <p>
              <span className="font-medium text-foreground">Role:</span> {role || 'General'}
            </p>
            <p>
              <span className="font-medium text-foreground">Level:</span>{' '}
              {difficulty === 'entry' ? 'Entry' : difficulty === 'mid' ? 'Mid' : 'Senior'}
            </p>
            <p className="text-xs pt-1 border-t border-border mt-2">
              You'll answer one question, get scored, then can try another. Drills don't count
              against your session limit.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={startDrill}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Start Drill →
            </button>
            <Link
              to="/dashboard"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground text-sm animate-pulse">Thinking…</p>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-destructive text-sm">{errorMsg}</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={startDrill}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground">
            ← Dashboard
          </Link>
        </div>
      </div>
    )
  }

  // ── Questioning ────────────────────────────────────────────────────────────
  if (phase === 'questioning' && currentQuestion) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-start px-4 py-12 bg-background">
        <div className="w-full max-w-lg space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {isFollowUp ? 'Follow-up' : `Drill · ${label}`}
            </p>
            <Link to="/dashboard" className="text-xs text-muted-foreground hover:text-foreground">
              ← Dashboard
            </Link>
          </div>

          <div className="rounded-xl border border-border bg-muted/30 px-5 py-4 text-sm leading-relaxed">
            {currentQuestion.text}
          </div>

          {tip && (
            <p className="text-xs text-amber-700 dark:text-amber-400 italic">💡 Remember: {tip}</p>
          )}

          <div className="relative">
            <textarea
              value={draft}
              maxLength={ANSWER_TEXT_MAX_CHARS}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={canDictate ? 'Type or speak your answer…' : 'Type your answer here…'}
              rows={6}
              className="w-full rounded-xl border border-border bg-background px-4 py-3 pr-10 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            />
            <DictationMic
              isListening={isListening}
              isSupported={canDictate}
              error={sttError}
              disabled={false}
              interimTranscript={interimTranscript}
              onToggle={handleMicClick}
            />
          </div>

          {errorMsg && (
            <p
              role="alert"
              className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              {errorMsg}
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {draft.trim().split(/\s+/).filter(Boolean).length} words
            </span>
            <button
              type="button"
              onClick={submitAnswer}
              disabled={!draft.trim()}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {errorMsg ? 'Retry →' : 'Send →'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Feedback ───────────────────────────────────────────────────────────────
  if (phase === 'feedback' && scores) {
    const feedbackItems = [
      {
        label: 'Situation',
        weight: '20%',
        score: scores.situation_score,
        text: feedback?.situationFeedback,
      },
      { label: 'Task', weight: '15%', score: scores.task_score, text: feedback?.taskFeedback },
      {
        label: 'Action',
        weight: '40%',
        score: scores.action_score,
        text: feedback?.actionFeedback,
      },
      {
        label: 'Result',
        weight: '25%',
        score: scores.result_score,
        text: feedback?.resultFeedback,
      },
    ]

    return (
      <div className="min-h-screen flex flex-col items-center justify-start px-4 py-12 bg-background">
        <div className="w-full max-w-lg space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Drill · {label}
              </p>
              {roundCount > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">Round {roundCount} complete</p>
              )}
            </div>
            <Link to="/dashboard" className="text-xs text-muted-foreground hover:text-foreground">
              ← Dashboard
            </Link>
          </div>

          {/* STAR Scores */}
          <div className="rounded-xl border border-border bg-background px-5 py-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
              Your STAR Scores
            </p>
            {feedbackItems.map((item) => (
              <ScoreBar
                key={item.label}
                label={item.label}
                score={item.score}
                weight={item.weight}
              />
            ))}
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Overall</span>
              <span
                className={cn('text-sm font-bold tabular-nums', scoreColor(scores.overall_score))}
              >
                {scores.overall_score != null ? scores.overall_score.toFixed(1) : '—'}/5
              </span>
            </div>
          </div>

          {/* Per-component feedback — always expanded */}
          {feedbackItems.some((i) => i.text) && (
            <div className="rounded-xl border border-border bg-muted/20 px-5 py-4 space-y-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                Feedback
              </p>
              {feedbackItems.map(
                (item) =>
                  item.text && (
                    <div key={item.label} className="space-y-1">
                      <p className="text-xs font-semibold">{item.label}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                    </div>
                  )
              )}
            </div>
          )}

          {/* Coaching tip reminder */}
          <CoachingTip tip={tip} />

          {/* CTAs */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={startDrill}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Try another →
            </button>
            <Link
              to="/dashboard"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return null
}
